import * as angular from "angular"
import { observeResize } from "../../components/resizeObserver"
import { IOmniaShell } from "../../shell"
import { defineDirective } from "../../utils/angular"
import { isArrayEmpty } from "../../utils/array"
import { IMapContainer, IMapKey, IMapTooltip } from "../types"
import { BasemapKeyViewModel, getVisibleBasemapKeysWatcher } from "./keys"
import { showTextArea } from "../../shell/msgbox/msgbox"

export function createMapView(): ng.IDirective {

    const def = defineDirective(MapViewCtrl, "shared/map/view/mapView.html", {
        map: "=",
        isActive: "="
    });

    def.link = (scope, element, attrs, ctrl: MapViewCtrl) => {

        ctrl.linkMapElement(element);

    };

    return def;

};

/** @internal */
export class MapViewCtrl {

    private _rootElement: ng.IAugmentedJQuery
    private _mapElement: HTMLElement

    private _resizeObserver: Function
    private _keyWatcher: Function

    private _mapWidth: number;
    private _mapHeight: number;

    map: IMapContainer;

    public isActive: boolean;
    public isSearchExpanded: boolean;

    private _locationExtent: any;

    public showToolsPopup: boolean;

    private _collapsedKeys: { [id: string]: boolean } = {}

    public basemapKeys: BasemapKeyViewModel[]

    private _showDetailsBox: boolean

    static $inject = ["$scope", "$compile", "OmniaShell"];

    constructor(private $scope: ng.IScope, private $compile: ng.ICompileService, private app: IOmniaShell) {
    }

    $onInit() {

        const { $scope } = this

        $scope.$watch("vm.map", () => this.setupMap())

        $scope.$watch("vm.isSearchExpanded", () => {
            if (this.isSearchExpanded) {
                document.getElementById('mapSearch').focus();
            }
        })

        $scope.$watch("vm.map.detailsBox", () => this.setupDetailsBox())

    }

    $onDestroy() {
        this.dispose()
    }

    private dispose() {

        if (this._resizeObserver) {
            this._resizeObserver()
            this._resizeObserver = undefined
        }

        if (this._keyWatcher) {
            this._keyWatcher()
            this._keyWatcher = undefined
        }

    }

    private setupMap() {

        this.dispose()

        if (!this.map) return
        if (!this._mapElement) return

        let map = this.map.map

        map.setTarget(this._mapElement)

        this._resizeObserver = observeResize(this._mapElement, () => this.updateSize(), 1000)

        this._keyWatcher = this.$scope.$watch(
            getVisibleBasemapKeysWatcher(this.map),
            keys => this.basemapKeys = keys
        )

    }

    public linkMapElement(elt: ng.IAugmentedJQuery) {

        this._rootElement = elt
        this._mapElement = elt[0].querySelector(".map-container")

        this.setupMap();

    }

    get mapViewCssClass() {
        return this.map && this.map.map.mapViewCssClass
    }

    get locationExtent() {
        return this._locationExtent;
    }

    set locationExtent(value) {

        this._locationExtent = value;

        if (this.map) {
            this.map.viewExtent(value);
        }

    }

    get tooltip(): IMapTooltip {
        return (this.map ? this.map.tooltip : undefined);
    }

    set tooltip(value: IMapTooltip) {
        if (this.map) {
            this.map.tooltip = value;
        }
    }

    get tooltipPositionX(): number {
        if (this.map && this.map.tooltip) {
            return this.tooltip.screenPosition[0];
        }
    }

    get tooltipPositionY(): number {
        if (this.map && this.map.tooltip) {
            return this.tooltip.screenPosition[1];
        }
    }

    get tooltipContext(): any {
        if (this.map && this.map.tooltip) {
            return this.map.tooltip.context;
        }
    }

    get tooltipData(): any {
        if (this.map && this.map.tooltip) {
            return this.map.tooltip.data;
        }
    }

    get keys() {
        return this.map ? this.map.keys : undefined
    }

    get allowTracking() {
        const { map } = this
        return map && map.allowTracking
    }

    get canViewTrackingInfo() {
        const { map } = this
        return map && map.allowTracking
    }

    getTracking(): boolean {
        return this.map && this.map.tracking;
    }

    setTracking() {
        this.map.setTracking(!this.map.tracking, this.canViewTrackingInfo);
    }

    showTrackingInfo() {
        const info = JSON.stringify(this.map.getTrackingInfo(), null, 2)
        showTextArea(this.app, info)
    }

    private updateSize() {

        const map = this.map?.getMap()
        if (!map) return

        const mapElement = this._mapElement
        if (!mapElement) return;

        if (!this.isActive) return;

        var rect = mapElement.getBoundingClientRect();

        var w = rect.width;
        var h = rect.height;

        if (w && h && ((this._mapWidth != w) || (this._mapHeight != h))) {
            this._mapWidth = w;
            this._mapHeight = h;
            map.updateSize();
        }
    }

    viewDefaultExtent() {
        this.map.viewDefaultExtent();
    }

    private openToolsPopup(open: boolean = true) {
        this.showToolsPopup = open;
        let tool = this.map.getMeasuringTool();
        tool.setActive(open);
    }

    private closeToolsPopup() {
        this.openToolsPopup(false);
    }

    private hasMeasures() {
        return this.map.getMeasuringTool().hasMeasures();
    }

    private clearMeasures() {
        return this.map.getMeasuringTool().clearMeasures();
    }

    private startMeasuringArea() {
        let tool = this.map.getMeasuringTool();
        tool.startMeasuringArea();
    }

    private startMeasuringLength() {
        let tool = this.map.getMeasuringTool();
        tool.startMeasuringLength();
    }

    private isDrawingNewMeasure() {
        return this.map.getMeasuringTool().isDrawingNewMeasure();
    }

    toggleSearch($event: ng.IAngularEvent) {

        if ($event && $event["ctrlKey"]) {
            let extent = this.map.getCurrentViewExtent();
            let xmin = extent[0], ymin = extent[1], xmax = extent[2], ymax = extent[3];
            let wkt = `POLYGON((${xmin} ${ymax}, ${xmax} ${ymax}, ${xmax} ${ymin}, ${xmin} ${ymin}, ${xmin} ${ymax}))`;
            console.log(extent, wkt);
        }

        this.isSearchExpanded = !this.isSearchExpanded;

    }

    private setupDetailsBox() {

        this._showDetailsBox = false

        const detailsBoxElt = angular.element(this._rootElement[0].querySelector(".details-box"))

        if (!(this._rootElement && this.map && this.map.detailsBox)) {
            detailsBoxElt.empty()
            return
        }

        const contents = this.map.detailsBox

        const scope: any = this.$scope.$new(true)

        scope.context = {
            map: this.map,
            ...contents.context
        }

        scope.setVisibility = (visible: boolean) => this._showDetailsBox = visible

        const template = `<${contents.component}></${contents.component}>`

        const link = this.$compile(template)

        const elt = link(scope)

        detailsBoxElt.empty().append(elt)

    }

    showDetailsBox() {

        const { map, _showDetailsBox: show } = this
        if (!(map && show)) return false

        const { detailsBox: box } = map
        return box && box.isVisible()

    }

    showBasemapKeys() {
        return !isArrayEmpty(this.basemapKeys)
    }

    isKeyExpanded(key: IMapKey) {
        return !this._collapsedKeys[key.id]
    }

    toggleKey(key: IMapKey) {
        const ck = this._collapsedKeys
        ck[key.id] = !ck[key.id]
    }

}


